import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { knowledgeApi } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Search, Download, Eye, FileText, Users, GraduationCap, Monitor,
  Heart, ImageIcon, Loader2, Star, ChevronRight, X, Settings
} from 'lucide-react';

const CATEGORIES = [
  { id: 'all', label: 'Alla' },
  { id: 'personal', label: 'Personal', icon: Users },
  { id: 'skolledning', label: 'Skolledning', icon: GraduationCap },
  { id: 'it', label: 'IT', icon: Monitor },
  { id: 'vardnadshavare', label: 'Vårdnadshavare', icon: Heart },
  { id: 'skyltar', label: 'Skyltar & mallar', icon: ImageIcon },
];

const CATEGORY_STYLES = {
  personal: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20',
  skolledning: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/20',
  it: 'text-violet-600 bg-violet-50 dark:text-violet-400 dark:bg-violet-900/20',
  vardnadshavare: 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/20',
  skyltar: 'text-rose-600 bg-rose-50 dark:text-rose-400 dark:bg-rose-900/20',
};

function DocumentRow({ doc, onView, onDownload, downloading }) {
  const style = CATEGORY_STYLES[doc.category] || CATEGORY_STYLES.personal;
  const isLoading = downloading === doc.slug;

  return (
    <div
      className="group flex items-center gap-4 p-4 rounded-lg border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all cursor-pointer"
      onClick={() => onView(doc)}
      data-testid={`doc-card-${doc.slug}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${style}`}>
        <FileText className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-ibm font-semibold text-sm text-navy dark:text-white leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
          {doc.title}
        </h3>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-ibm line-clamp-1">
          {doc.description}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        {doc.has_pdf && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-400 hover:text-navy"
            onClick={e => { e.stopPropagation(); onDownload(doc); }}
            disabled={isLoading}
            data-testid={`download-${doc.slug}-btn`}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </Button>
        )}
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>
    </div>
  );
}

function RecommendedCard({ doc, onView }) {
  const style = CATEGORY_STYLES[doc.category] || CATEGORY_STYLES.personal;
  return (
    <Card
      className="border-slate-200 dark:border-slate-700 hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600 transition-all cursor-pointer group"
      onClick={() => onView(doc)}
      data-testid={`rec-${doc.slug}`}
    >
      <CardContent className="p-5">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${style}`}>
          <FileText className="h-4 w-4" />
        </div>
        <h3 className="font-ibm font-semibold text-sm text-navy dark:text-white leading-tight group-hover:text-blue-600 transition-colors">
          {doc.title}
        </h3>
        <p className="text-xs text-slate-400 mt-1.5 font-ibm line-clamp-2">{doc.description}</p>
      </CardContent>
    </Card>
  );
}

export default function KnowledgeBasePage() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsRes, recRes] = await Promise.all([
        knowledgeApi.documents(),
        knowledgeApi.recommended(),
      ]);
      setDocuments(docsRes.data.documents || []);
      setRecommended(recRes.data.documents || []);
    } catch {
      toast.error('Kunde inte ladda kunskapsbanken');
    } finally {
      setLoading(false);
    }
  };

  const filteredDocs = useMemo(() => {
    let filtered = documents;
    if (activeCategory !== 'all') {
      filtered = filtered.filter(d => d.category === activeCategory);
    }
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(d =>
        d.title.toLowerCase().includes(s) || d.description.toLowerCase().includes(s)
      );
    }
    return filtered;
  }, [documents, activeCategory, search]);

  const handleView = (doc) => navigate(`/knowledge/${doc.slug}`);

  const handleDownload = async (doc) => {
    setDownloading(doc.slug);
    try {
      const endpoint = doc.pdf_endpoint.startsWith('/api/')
        ? doc.pdf_endpoint.replace('/api/', '/')
        : doc.pdf_endpoint;
      const res = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vapelarm_${doc.slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`${doc.title} nedladdad`);
    } catch {
      toast.error('Kunde inte ladda ner dokumentet');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const showRecommended = !search && activeCategory === 'all' && recommended.length > 0;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto space-y-8" data-testid="knowledge-base-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy dark:text-white tracking-tight">
            Kunskapsbank
          </h1>
          <p className="text-slate-400 dark:text-slate-500 mt-1 font-ibm text-sm max-w-lg">
            Guider, mallar och information för att använda Vapelarm Cloud.
          </p>
        </div>
        {isSuperAdmin() && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/knowledge/admin')}
            className="shrink-0 gap-1.5 text-slate-400"
            data-testid="manage-knowledge-btn"
          >
            <Settings className="h-3.5 w-3.5" />
            Hantera
          </Button>
        )}
      </div>

      {/* Academy banner */}
      <button
        onClick={() => navigate('/academy')}
        className="w-full flex items-center gap-4 p-4 rounded-lg bg-navy text-white text-left hover:bg-navy/90 transition-colors"
        data-testid="knowledge-academy-banner"
      >
        <GraduationCap className="h-6 w-6 text-red-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-chivo font-bold text-sm">Vapelarm Academy</p>
          <p className="text-xs text-slate-300 font-ibm">Manualer, interaktiva guider och din personliga checklista</p>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </button>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
        <Input
          placeholder="Sök dokument..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-10 h-11 font-ibm bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"
          data-testid="knowledge-search-input"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="h-4 w-4 text-slate-300 hover:text-slate-500" />
          </button>
        )}
      </div>

      {/* Recommended */}
      {showRecommended && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
            <h2 className="font-ibm font-semibold text-xs uppercase tracking-wider text-slate-400">Rekommenderat för dig</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {recommended.slice(0, 3).map(doc => (
              <RecommendedCard key={doc.id} doc={doc} onView={handleView} />
            ))}
          </div>
        </div>
      )}

      {/* Category tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1 -mb-2">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-ibm font-medium whitespace-nowrap transition-colors ${
              activeCategory === cat.id
                ? 'bg-navy text-white'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
            data-testid={`category-${cat.id}`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Document list */}
      <div>
        {filteredDocs.length > 0 ? (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {filteredDocs.map(doc => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                onView={handleView}
                onDownload={handleDownload}
                downloading={downloading}
              />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center">
            <FileText className="h-10 w-10 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
            <p className="text-slate-400 font-ibm text-sm">Inga dokument hittades</p>
          </div>
        )}
      </div>
    </div>
  );
}
